import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { doc, getDoc, collection, addDoc, query, where, getDocs } from 'firebase/firestore';
import { Timestamp } from 'firebase/firestore';
import { db } from '../../firebase';
import authService from '../../services/authService';
import { ArrowLeft, HelpCircle, Check } from 'lucide-react';
import './ExecutionQuizRun.css';

const QUIZ_POOLS_COLLECTION = 'quiz_pools';
const QUIZ_ATTEMPTS_COLLECTION = 'quiz_attempts';
const DEFAULT_PASS_PERCENT = 70;

/**
 * Run a quiz from a quiz_pool. Shows one question at a time; on complete persists attempt, shows results, then "Back to courses".
 */
export default function ExecutionQuizRun({ poolId, onBack, passThresholdPercent = DEFAULT_PASS_PERCENT }) {
  const { t } = useTranslation('execution');
  const uid = authService.getCurrentUser()?.uid;
  const startedAtRef = useRef(null);
  const [pool, setPool] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [selectedOptionId, setSelectedOptionId] = useState(null);
  const [answers, setAnswers] = useState({}); // questionId -> optionId
  const [showResults, setShowResults] = useState(false);

  useEffect(() => {
    if (!poolId) {
      setLoading(false);
      return;
    }
    const load = async () => {
      try {
        setLoading(true);
        const ref = doc(db, QUIZ_POOLS_COLLECTION, poolId);
        const snap = await getDoc(ref);
        if (!snap.exists()) {
          setError('Quiz not found');
          setPool(null);
          return;
        }
        setPool({ id: snap.id, ...snap.data() });
        setError(null);
      } catch (e) {
        setError(e.message || 'Failed to load quiz');
        setPool(null);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [poolId]);

  useEffect(() => {
    if (pool?.questions?.length && !startedAtRef.current) {
      startedAtRef.current = Timestamp.now();
    }
  }, [pool]);

  const questions = pool?.questions || [];
  const question = questions[currentIndex];
  const isLast = currentIndex === questions.length - 1;

  const results = useMemo(() => {
    if (questions.length === 0) return null;
    const correctCount = questions.filter((q) => {
      const chosen = answers[q.questionId];
      const correctOpt = (q.options || []).find((o) => o.isCorrect);
      return correctOpt && chosen === correctOpt.optionId;
    }).length;
    const percent = Math.round((correctCount / questions.length) * 100);
    const passed = percent >= passThresholdPercent;
    return {
      correctCount,
      total: questions.length,
      percent,
      passed,
      details: questions.map((q) => {
        const chosen = answers[q.questionId];
        const chosenOpt = (q.options || []).find((o) => o.optionId === chosen);
        const correctOpt = (q.options || []).find((o) => o.isCorrect);
        const correct = correctOpt && chosen === correctOpt.optionId;
        return {
          questionText: q.questionText,
          correct,
          chosenText: chosenOpt?.text ?? '',
          correctText: correctOpt?.text ?? '',
        };
      }),
    };
  }, [questions, answers, passThresholdPercent]);

  const handleSelect = (optionId) => {
    setSelectedOptionId(optionId);
    if (question?.questionId) {
      setAnswers((prev) => ({ ...prev, [question.questionId]: optionId }));
    }
  };

  const handleNext = () => {
    if (isLast) {
      setShowResults(true);
      return;
    }
    setCurrentIndex((i) => i + 1);
    setSelectedOptionId(answers[questions[currentIndex + 1]?.questionId] ?? null);
  };

  useEffect(() => {
    if (!showResults || !results || !pool || !uid) return;
    const persistAttempt = async () => {
      try {
        const protocolVersionId = pool.protocolVersionId || poolId?.split('_')[0];
        const userGroupId = pool.userGroupId || poolId?.split('_')[1] || '';
        const completedAt = Timestamp.now();
        const startedAt = startedAtRef.current || completedAt;
        const answersPayload = (pool.questions || []).map((q) => {
          const chosen = answers[q.questionId];
          const correctOpt = (q.options || []).find((o) => o.isCorrect);
          const isCorrect = Boolean(correctOpt && chosen === correctOpt.optionId);
          return {
            questionId: q.questionId,
            selectedOptionId: chosen || '',
            isCorrect,
            answeredAt: completedAt,
          };
        });
        let attemptNumber = 1;
        try {
          const q = query(
            collection(db, QUIZ_ATTEMPTS_COLLECTION),
            where('staffId', '==', uid),
          );
          const snap = await getDocs(q);
          const samePool = snap.docs.filter(
            (d) => d.data().protocolVersionId === protocolVersionId && d.data().userGroupId === userGroupId,
          );
          attemptNumber = samePool.length + 1;
        } catch (e) {
          if (process.env.NODE_ENV === 'development') console.warn('Quiz attempt number fallback', e);
        }
        await addDoc(collection(db, QUIZ_ATTEMPTS_COLLECTION), {
          staffId: uid,
          protocolVersionId,
          userGroupId,
          attemptNumber,
          startedAt,
          completedAt,
          score: results.percent,
          passed: results.passed,
          sessionId: `s_${Date.now()}`,
          answers: answersPayload,
          status: 'completed',
        });
      } catch (e) {
        if (process.env.NODE_ENV === 'development') console.warn('Quiz attempt persist failed', e);
      }
    };
    persistAttempt();
  }, [showResults, results, pool, uid, answers, poolId]);

  if (!poolId) return null;

  if (loading) {
    return (
      <div className="execution-quiz-run">
        <p className="execution-quiz-loading">{t('tasks:loadingTasks', 'Se încarcă...')}</p>
      </div>
    );
  }

  if (error || !pool) {
    return (
      <div className="execution-quiz-run">
        <p className="execution-quiz-error">{error || 'Quiz not found'}</p>
        <button type="button" className="execution-quiz-back" onClick={onBack}>
          <ArrowLeft size={20} /> {t('trainingMaterials')}
        </button>
      </div>
    );
  }

  if (questions.length === 0) {
    return (
      <div className="execution-quiz-run">
        <p className="execution-quiz-empty">{t('learnNoQuestions', 'Nicio întrebare în acest quiz.')}</p>
        <button type="button" className="execution-quiz-back" onClick={onBack}>
          <ArrowLeft size={20} /> {t('trainingMaterials')}
        </button>
      </div>
    );
  }

  if (showResults && results) {
    return (
      <div className="execution-quiz-run execution-quiz-results">
        <div className="execution-quiz-results-header">
          <span className="execution-quiz-results-id">{pool.protocolVersionId || poolId}</span>
          <div className={`execution-quiz-results-badge ${results.passed ? 'passed' : 'failed'}`}>
            {results.passed
              ? t('learnQuizPassed', 'Promovat!')
              : t('learnQuizFailed', 'Nepromovat')}
          </div>
          <p className="execution-quiz-results-score">{results.percent}%</p>
          <p className="execution-quiz-results-count">
            {results.correctCount} {t('learnOutOf', 'din')} {results.total} {t('learnCorrect', 'corecte')}
          </p>
        </div>
        <div className="execution-quiz-results-list">
          {results.details.map((item, idx) => (
            <div
              key={idx}
              className={`execution-quiz-results-item ${item.correct ? 'correct' : 'incorrect'}`}
            >
              <p className="execution-quiz-results-question">{item.questionText}</p>
              {item.correct ? (
                <p className="execution-quiz-results-answer execution-quiz-results-answer-correct">
                  <Check size={18} /> {item.correctText}
                </p>
              ) : (
                <>
                  <p className="execution-quiz-results-answer execution-quiz-results-answer-wrong">
                    {t('learnYouAnswered', 'Ai răspuns')}: {item.chosenText}
                  </p>
                  <p className="execution-quiz-results-answer execution-quiz-results-answer-correct">
                    {t('learnCorrectLabel', 'Corect')}: {item.correctText}
                  </p>
                </>
              )}
            </div>
          ))}
        </div>
        <div className="execution-quiz-results-footer">
          <button type="button" className="execution-quiz-results-back-btn" onClick={onBack}>
            {t('learnBackToCourses', 'Înapoi la cursuri')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="execution-quiz-run">
      <header className="execution-quiz-header">
        <button type="button" className="execution-quiz-back" onClick={onBack}>
          <ArrowLeft size={20} /> {t('trainingMaterials')}
        </button>
        <span className="execution-quiz-progress">
          {currentIndex + 1} / {questions.length}
        </span>
      </header>
      <div className="execution-quiz-question">
        <div className="execution-quiz-question-icon">
          <HelpCircle size={32} />
        </div>
        <h2 className="execution-quiz-question-text">{question.questionText}</h2>
        <ul className="execution-quiz-options">
          {(question.options || []).map((opt) => (
            <li key={opt.optionId}>
              <button
                type="button"
                className={`execution-quiz-option ${selectedOptionId === opt.optionId ? 'selected' : ''}`}
                onClick={() => handleSelect(opt.optionId)}
              >
                {opt.text}
              </button>
            </li>
          ))}
        </ul>
      </div>
      <footer className="execution-quiz-footer">
        <button
          type="button"
          className="execution-quiz-next"
          onClick={handleNext}
          disabled={!selectedOptionId}
        >
          {isLast ? t('learnFinishQuiz', 'Finalizează') : t('learnNext', 'Următorul')} →
        </button>
      </footer>
    </div>
  );
}
